import { RightOutlined } from '@ant-design/icons';
import { Carousel, Col, Image, Row, Typography } from 'antd';
import CaregiverAvatarList from 'components/lists/CaregiverAvatarList';
import Logo from 'components/navigation/Logo';
import { Button } from 'components/ui';
import { colors, screenHeight } from 'design';
import OnboardingV2Page from 'pages/OnboardingV2';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';

const { Title, Paragraph, Text } = Typography;

const useStyles = createUseStyles({
  carousel: {
    backgroundColor: colors.surface.page,
    background: `linear-gradient(0deg, ${colors.surface.page} 0%, white 100%)`,
    backgroundSize: 'auto 100px',
    backgroundPosition: 'top',
    backgroundRepeat: 'no-repeat',
  },
  carouselPageContainer: {
    display: 'flex !important',
    height: screenHeight,
  },
  carouselPage: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  carouselPageSection: {
    justifyContent: 'center',
    textAlign: 'center',
    padding: 10,
  },
  carouselPageSectionFullPage: {
    justifyContent: 'center',
    textAlign: 'center',
    padding: 10,
    flex: 1,
  },
  carouselPageSectionFullPageNoGrow: {
    justifyContent: 'center',
    textAlign: 'center',
    padding: 10,
    flex: 0,
  },
  carouselPageSectionFullPageScroll: {
    justifyContent: 'center',
    textAlign: 'center',
    padding: 10,
    flex: 1,
    overflow: 'scroll',
  },
  carouselPageSectionBottom: {
    justifyContent: 'center',
    textAlign: 'center',
    padding: 50,
  },
  carouselTitle: {},
  appName: {
    color: colors.brand.accentStrong,
  },
  textStrongGreen: {
    color: colors.brand.accentStrong,
  },
  contentImageContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  contentImage: {
    marginTop: 16,
    width: '75%',
  },
  cardColumn: {
    padding: '0',
    display: 'flex',
    justifyContent: 'center',
  },
  cardIcon: {
    minWidth: 80,
    maxWidth: 80,
  },
  // TODO harmonize icons: have only svg
  cardSvgIcon: {
    height: 80,
    color: colors.brand.accentStrong,
    fontSize: 80 * 0.75,
  },
  secondRow: {
    marginTop: 16,
  },
  dots: {
    bottom: '20px',

    '& li > button': {
      backgroundColor: colors.text.tertiary,
    },
    '& li.slick-active > button': {
      backgroundColor: colors.brand.accent,
    },
  },
});

export default function OnboardingPage() {
  const variant = posthog.getFeatureFlag('onboarding-slides-v3');
  if (variant === 'new-slides') {
    return <OnboardingV2Page />;
  }
  if (variant === 'no-slides') {
    return <NoSlidesOnboardingPage />;
  }
  return <OnboardingLegacyPage />;
}

// Skips the carousel entirely and sends the user straight to whatever the
// end of the carousel would have led to.
function NoSlidesOnboardingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(routes.home.route, { replace: true });
  }, [navigate]);

  return null;
}

function OnboardingLegacyPage() {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const styles = useStyles();
  const carousel = useRef(null);

  const goToPage = (route: string) => {
    navigate(route);
  };

  const nextPage = (isLastPage: boolean) => {
    if (isLastPage) {
      goToPage(routes.home.route);
    } else {
      // Display the next page
      (carousel?.current as any)?.next();
    }
  };

  const carouselPages = [
    // Page 1
    {
      content: (
        <div>
          <Paragraph>
            <Text>
              {t('onboarding.carousel.pages.one.text1start')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.one.text1Bold1')}
              </Text>
              {t('onboarding.carousel.pages.one.text1middle')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.one.text1Bold2')}
              </Text>
              {t('onboarding.carousel.pages.one.text1end')}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text>
              {t('onboarding.carousel.pages.one.text2start')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.one.text2Bold1')}
              </Text>
              {t('onboarding.carousel.pages.one.text2middle')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.one.text2Bold2')}
              </Text>
            </Text>
          </Paragraph>
        </div>
      ),
      scrollableContent: (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CaregiverAvatarList />
        </div>
      ),
    },

    // Page 2
    {
      scrollableContent: (
        <div>
          <Paragraph>
            <Text>
              {t('onboarding.carousel.pages.tracking.textStart')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.tracking.textBold1')}
              </Text>
              {t('onboarding.carousel.pages.tracking.textMiddle')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.tracking.textBold2')}
              </Text>
              {t('onboarding.carousel.pages.tracking.textEnd')}
            </Text>
          </Paragraph>
          <div className={styles.contentImageContainer}>
            <Image
              alt="Caregivers list"
              src={require('../assets/onboarding-tracking.png')}
              preview={false}
              rootClassName={styles.contentImage}
            />
          </div>
        </div>
      ),
    },

    // Page 3
    {
      scrollableContent: (
        <div>
          <Paragraph>
            <Text>
              {t('onboarding.carousel.pages.two.textStart')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.two.textBold1')}
              </Text>
              {t('onboarding.carousel.pages.two.textMiddle1')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.two.textBold2')}
              </Text>
              {t('onboarding.carousel.pages.two.textMiddle2')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.two.textBold3')}
              </Text>
              {t('onboarding.carousel.pages.two.textMiddle3')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.two.textBold4')}
              </Text>
            </Text>
          </Paragraph>
          <div className={styles.contentImageContainer}>
            <Image
              alt="Blog post samples"
              src={require('../assets/onboarding-blog-posts.png')}
              preview={false}
              rootClassName={styles.contentImage}
            />
          </div>
        </div>
      ),
    },

    // Page 4
    {
      scrollableContent: (
        <div>
          <Paragraph>
            <Text>
              {t('onboarding.carousel.pages.three.textStart')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.three.textBold1')}
              </Text>
              {t('onboarding.carousel.pages.three.textMiddle1')}
              <Text strong className={styles.textStrongGreen}>
                {t('onboarding.carousel.pages.three.textBold2')}
              </Text>
              {t('onboarding.carousel.pages.three.textEnd')}
            </Text>
          </Paragraph>
          <div className={styles.contentImageContainer}>
            <Image
              alt="Caregivers list"
              src={require('../assets/onboarding-caregivers-list.png')}
              preview={false}
              rootClassName={styles.contentImage}
            />
          </div>
        </div>
      ),
    },
  ];

  const renderOnboardingCarouselPageTemplate = (
    index: number,
    children: { content?: React.ReactElement; scrollableContent?: React.ReactElement },
  ) => {
    const isLastPage = index === carouselPages.length - 1;
    const buttonText = isLastPage ? t('onboarding.carousel.buttons.end') : t('onboarding.carousel.buttons.next');

    return (
      <div key={index} className={styles.carouselPageContainer}>
        <div className={styles.carouselPage}>
          <Row className={styles.carouselPageSection}>
            <Col style={{ paddingTop: 64 }}>
              <Logo size="large" />
            </Col>
          </Row>
          <Row className={styles.carouselPageSection}>
            <Col>
              <Title level={3}>
                {t('onboarding.carousel.title')}
                <span className={styles.appName}>{t('appName')}</span>
                {t('onboarding.carousel.titleEnd')}
              </Title>
              <Paragraph>
                <span>{t('appTagline')}</span>
              </Paragraph>
            </Col>
          </Row>
          {children.content && (
            <Row className={styles.carouselPageSectionFullPageNoGrow}>
              <Col>{children.content}</Col>
            </Row>
          )}
          {children.scrollableContent && (
            <Row className={styles.carouselPageSectionFullPageScroll}>
              <Col>{children.scrollableContent}</Col>
            </Row>
          )}
          <Row className={styles.carouselPageSectionBottom}>
            <Col>
              <Button
                variant="primary"
                onClick={() => nextPage(isLastPage)}
                icon={<RightOutlined />}
                iconPosition="end"
                size="lg"
              >
                {buttonText}
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  return (
    <Carousel
      ref={carousel}
      className={styles.carousel}
      adaptiveHeight
      infinite={false}
      dots={{ className: styles.dots }}
    >
      {carouselPages.map((page, index) => renderOnboardingCarouselPageTemplate(index, page))}
    </Carousel>
  );
}
